
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./subdivisionmarkup";
import { usePage } from "../hooks/usePage";

import {
  getSubDivisions,
  createSubDivision,
  getSubDivisionById,
  updateSubDivision,
  clearSubDivisionError,
} from "../store/slices/subdivision";

import { getOfficers } from "../store/slices/user";

const PageMarkup = memo(Markup);

export default function SubDivisions() {
  usePage({
    title: "All Sub Divisions - Nellore Police Dashboard",
    bodyClass,
  });

  const dispatch = useDispatch();

  // Sub-division Redux state
  const {
    subdivisions = [],
    pagination,
    loading,
    error,
    detailsLoading,
    createLoading,
    updateLoading,
  } = useSelector((state) => state.subdivision);

  // User / Officer Redux state
  const {
    officers: dspUsers = [],
    loading: usersLoading,
  } = useSelector((state) => state.user);

  const total = pagination?.total || 0;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");

  // Fetch sub-divisions
  useEffect(() => {
    dispatch(
      getSubDivisions({
        page: 1,
        limit: 1000,
      })
    );
  }, [dispatch]);

  // Fetch only active DSP officers
  const handleGetUsers = useCallback(async () => {
    const response = await dispatch(
      getOfficers({
        designation: "DSP",
        active: true,
        page: 1,
        limit: 1000,
      })
    ).unwrap();

    return response?.officers || [];
  }, [dispatch]);

  // Search and status filtering
  const filteredSubDivisions = useMemo(() => {
    return subdivisions.filter((subdivision) => {
      const searchText = search.trim().toLowerCase();

      const matchesSearch =
        !searchText ||
        [
          subdivision.name,
          subdivision.subDivisionId,
          subdivision.dspName,
          subdivision.phone,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(searchText)
        );

      const matchesStatus =
        status === "ALL" ||
        (status === "ACTIVE" && subdivision.active === true) ||
        (status === "INACTIVE" && subdivision.active === false);

      return matchesSearch && matchesStatus;
    });
  }, [subdivisions, search, status]);

  // Statistics
  const statistics = useMemo(
    () => ({
      total: total || subdivisions.length,

      active: subdivisions.filter(
        (item) => item.active === true
      ).length,

      inactive: subdivisions.filter(
        (item) => item.active === false
      ).length,

      withDsp: subdivisions.filter(
        (item) => item.dspOfficerId || item.dspName
      ).length,
    }),
    [subdivisions, total]
  );

  // Fetch one sub-division for editing
  const handleGetSubdivision = async (id) => {
    const response = await dispatch(
      getSubDivisionById(id)
    ).unwrap();

    return (
      response?.subDivision ||
      response?.subdivision ||
      response?.data?.subDivision ||
      response?.data?.subdivision ||
      response
    );
  };

  // Create sub-division
  const handleCreateSubdivision = async (payload) => {
    await dispatch(
      createSubDivision(payload)
    ).unwrap();

    await dispatch(
      getSubDivisions({
        page: 1,
        limit: 1000,
      })
    ).unwrap();
  };

  // Update sub-division
  const handleUpdateSubdivision = async (id, payload) => {
    await dispatch(
      updateSubDivision({
        id,
        payload,
      })
    ).unwrap();

    await dispatch(
      getSubDivisions({
        page: 1,
        limit: 1000,
      })
    ).unwrap();
  };

  return (
    <PageMarkup
      subdivisions={filteredSubDivisions}
      totalSubDivisions={total || subdivisions.length}
      statistics={statistics}

      loading={loading}
      error={error}
      detailsLoading={detailsLoading}
      saving={createLoading || updateLoading}

      search={search}
      setSearch={setSearch}

      status={status}
      setStatus={setStatus}

      onAddSubdivision={() => {
        dispatch(clearSubDivisionError());
      }}

      onGetSubdivision={handleGetSubdivision}
      onCreateSubdivision={handleCreateSubdivision}
      onUpdateSubdivision={handleUpdateSubdivision}

      // DSP dropdown props
      dspUsers={dspUsers}
      usersLoading={usersLoading}
      onGetUsers={handleGetUsers}
    />
  );
}
